// Task 7.5: SEO Structured Data Components

import { CurrencyManager } from '@/lib/marketing/currency-manager';

type SchemaType = {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
};

export interface FaqItem {
  question: string;
  answer: string;
}

interface StructuredDataProps {
  type: 'organization' | 'software' | 'faq';
  data?: Record<string, unknown>;
  faqItems?: FaqItem[];
  nonce?: string;
}

export function StructuredData({ type, faqItems, nonce }: StructuredDataProps) {
  let schema: SchemaType = {
    '@context': 'https://schema.org',
    '@type': 'Thing'
  };

  switch (type) {
    case 'organization':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Scopax',
        url: 'https://scopax.com',
        logo: 'https://scopax.com/images/scopax-logo.png',
        description: 'Professional calibration management software for manufacturing companies in the UK & Ireland',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'CH',
          addressRegion: 'Switzerland',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@scopax.com',
          contactType: 'customer support',
          availableLanguage: ['English', 'German'],
        },
        sameAs: [
          'https://linkedin.com/company/scopax',
        ],
      };
      break;

    case 'software': {
      const cm = new CurrencyManager();
      const lowPrice = cm.getPlanPrice('STARTER', 'GBP');
      const highPrice = cm.getPlanPrice('PRO', 'GBP');
      const priceValidUntil = `${new Date().getFullYear() + 1}-12-31`;

      schema = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Scopax',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web Browser',
        description: 'Calibration management software for equipment tracking, audit pack generation, and ISO compliance in manufacturing',
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: String(lowPrice),
          highPrice: String(highPrice),
          priceCurrency: 'GBP',
          priceValidUntil,
          description: 'Monthly subscription for calibration management software',
        },
        creator: {
          '@type': 'Organization',
          name: 'Scopax',
        },
        featureList: [
          'Equipment calibration tracking',
          'Audit pack generation',
          'ISO compliance monitoring',
          'Certificate management',
          'Real-time notifications',
        ],
        screenshot: 'https://scopax.com/images/dashboard-screenshot.png',
      };
      break;
    }

    case 'faq':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (faqItems ?? []).map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      };
      break;
  }

  // In client contexts we cannot access next/headers; try to read a meta tag if present.
  let resolvedNonce: string | undefined = nonce;
  if (!resolvedNonce && typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="csp-nonce"], meta[property="csp-nonce"]') as HTMLMetaElement | null;
    resolvedNonce = meta?.content || undefined;
  }

  return (
    <script
      type="application/ld+json"
      // Attach CSP nonce so strict CSP allows this inline script
      nonce={resolvedNonce}
      // React intentionally removes nonce attributes during hydration for security.
      // This mismatch between server (nonce="xxx") and client (nonce="") is expected.
      // See: https://react.dev/reference/react-dom/components/script#special-rendering-behavior
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
